import { FunctionComponent } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
	animation,
	appearancePermutations,
	keyframes,
	ToggleButton,
	ToggleButtonProps,
} from "@animech/ui";

const ToggleButtonWithProps: FunctionComponent<ToggleButtonProps<boolean>> = ({
	appearance,
	color,
	...props
}) => (
	<ToggleButton
		{...props}
		appearance={appearance ? appearance : "solid"}
		color={color ? color : "neutral"}
		rounded
	/>
);

export const Icon = styled.div``;
export const IconLine = styled.div``;

export const Root = styled(ToggleButtonWithProps)`
	${({ theme, size = "default" }) => {
		const { toggleButton } = theme.components;

		return css`
			padding: ${toggleButton.sizes[size].paddingVertical} !important;
			min-width: 0;
			aspect-ratio: 1 / 1;

			${Icon} {
				position: relative;
				height: ${toggleButton.sizes[size].iconFontSize};
				width: ${toggleButton.sizes[size].iconFontSize};
				margin: 0 auto;
			}

			${IconLine} {
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translateY(0) translateX(-50%);
				height: 1px;
				width: calc(${toggleButton.sizes[size].iconFontSize} * 0.75);
				opacity: 1;
				transition:
					transform 0.25s,
					opacity 0.25s;
				transition-delay: 0.1s;

				&::after {
					display: block;
					height: 100%;
					width: 100%;
					content: "";
				}

				&:first-child,
				&:last-child {
					transition-delay: 0s;
					transition-timing-function: ${theme.animations.timingFunctions
						.spring};
				}

				&:first-child {
					transform: translateY(
							calc(${toggleButton.sizes[size].iconFontSize} * -0.3)
						)
						translateX(-50%);
				}

				&:last-child {
					transform: translateY(
							calc(${toggleButton.sizes[size].iconFontSize} * 0.3)
						)
						translateX(-50%);
				}
			}

			&:not([data-checked]) ${IconLine} {
				&::after {
					${animation({
						keyframes: keyframes.jello(),
						duration: 1000,
						delay: 150,
					})}
				}

				&:first-child::after {
					animation-delay: 0.2s;
				}

				&:last-child::after {
					animation-delay: 0.1s;
				}
			}

			&[data-checked] ${IconLine} {
				transform: translateY(-1px) translateX(-50%);
				opacity: 0;
				transition-delay: 0s;

				&::after {
					animation: none;
				}

				&:first-child,
				&:last-child {
					opacity: 1;
					transition-delay: 0.1s;
					transition-timing-function: ${theme.animations.timingFunctions
						.spring};
				}

				&:first-child {
					transform: translateY(-1px) translateX(-50%) rotate(135deg);
				}

				&:last-child {
					transform: translateY(-1px) translateX(-50%) rotate(-135deg);
				}
			}

			${appearancePermutations(toggleButton.colors).map(
				({ color, appearance, values: states }) => {
					return css`
						&[data-color="${color}"][data-appearance="${appearance}"] {
							${IconLine}::after {
								background: ${states.default.foreground};
							}

							&:hover ${IconLine}::after {
								background: ${states.hover.foreground};
							}

							&:active ${IconLine}::after {
								background: ${states.active.foreground};
							}

							&[data-checked] {
								${IconLine}::after {
									background: ${states.checked.foreground};
								}

								&:hover ${IconLine}::after {
									background: ${states.checkedHover.foreground};
								}

								&:active ${IconLine}::after {
									background: ${states.checkedActive.foreground};
								}
							}
						}
					`;
				},
			)}
		`;
	}}
`;
